import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const TLNode = ({ label, index, progress, total }) => {
  const isLeft = index % 2 === 0;
  const start = index / total;
  const opacity = useTransform(progress, [start - 0.05, start + 0.05], [0.15, 1]);
  const x = useTransform(progress, [start - 0.05, start + 0.05], [isLeft ? -40 : 40, 0]);
  const scale = useTransform(progress, [start - 0.03, start + 0.03], [0.4, 1]);

  return (
    <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8 min-h-[7.5rem]">
      <motion.div
        style={{ opacity: isLeft ? opacity : 0.15, x: isLeft ? x : 0 }}
        className={`text-right ${isLeft ? "" : "invisible"}`}
      >
        <Label label={label} index={index} align="right" />
      </motion.div>

      <div className="relative flex items-center justify-center w-6">
        <motion.div
          style={{ scale }}
          data-testid={`how-timeline-node-${index}`}
          className="w-3.5 h-3.5 rounded-full bg-gold ring-4 ring-gold/20 z-10"
        />
      </div>

      <motion.div
        style={{ opacity: !isLeft ? opacity : 0.15, x: !isLeft ? x : 0 }}
        className={`text-left ${!isLeft ? "" : "invisible"}`}
      >
        <Label label={label} index={index} align="left" />
      </motion.div>
    </div>
  );
};

const Label = ({ label, index, align }) => (
  <div>
    <p className={`overline text-earth/70 mb-1 ${align === "right" ? "text-right" : "text-left"}`}>
      {String(index + 1).padStart(2, "0")}
    </p>
    <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-light text-forest tracking-tight">
      {label}
    </h3>
  </div>
);

export const ScrollTimeline = ({ stages }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 70%", "end 80%"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <div ref={ref} className="relative max-w-3xl mx-auto px-6">
      <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px">
        <div className="absolute inset-0 bg-earth/15" />
        <motion.div
          style={{ scaleY: lineScale, transformOrigin: "top", ease: EASE }}
          className="absolute inset-0 bg-gradient-to-b from-gold via-earth to-forest"
        />
      </div>
      <div className="relative flex flex-col">
        {stages.map((label, i) => (
          <TLNode
            key={label}
            label={label}
            index={i}
            progress={scrollYProgress}
            total={stages.length}
          />
        ))}
      </div>
    </div>
  );
};
